import React, { useCallback, useEffect, useMemo } from 'react';
import { getFrontendType, isCORS, isNotEmpty, isSeamlessUrl, isTruthy } from 'utility/functions';

import { AuthError } from 'types/errors';
import { AuthErrorType } from 'types/login';
import { IconsEnum } from 'components/Icons';
import { defaultLang, isHappybetSite, isServerSide } from 'utility/constant';
import { mutex } from 'components/refreshTokenEngine';
import { setAlertMessage } from 'features/dashboard/dashboardSlice';
import { signIn } from 'next-auth/react';
import { useAppDispatch } from 'lib/centralStore';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useRouter } from 'next/router';

const isFeatureDisables = isCORS()

export const SeamlessLoginComponent = () => {
  const dispatch = useAppDispatch();

  const {asPath, locale} = useRouter();

  const {skip, path, params, seamless} = useMemo(() => {

    const seamless = isSeamlessUrl(asPath)

    const [path, query] = `${asPath}?`.split('?');
    const params = new URLSearchParams(query);

    const keys = Array.from(params.keys());
    const skip = isServerSide ||isFeatureDisables || ['snai-carta', 'snai-token', 'client-type'].some((key) => !keys.includes(key));

    return {
      skip,
      path,
      params,
      seamless,
    }
  }, [asPath]);

  const [blockedAccountLoginError, usernamePasswordError] = useGetLabelsByKeys([
    'blocked-account-login-error',
    'token-expired-error',
  ]);

  const doAuthenticate = useCallback(
    async (username, password, clientType) => {
      try {
        // lock other api calls during the login intervall
        await mutex.acquire();
      } catch (ex) {
        console.log('mutex-exc', ex);
      } finally {
        // address just history widhout reloadinging the page
        history.replaceState(history.state, '', `${path}${seamless ? '?seamless': ''}`)
      }

      // getFrontendType
      const credentials = {
        password,
        username,
        seamless,
        redirect: false,
        clientType,
        fromOldSite: true,
        frontendType: getFrontendType(),
      }

      if(isHappybetSite){        
        Reflect.set(credentials, 'lang', `${locale ?? defaultLang}`.substring(0, 2));
      }

      const response = await signIn('credentials', credentials);

      const isSuccess = isTruthy(response?.ok);

      if (isSuccess) {
        // await an arbitrary delay to allow new session data to be loaded
        await new Promise((resolve) => setTimeout(resolve, 499));
      } else {
        const { data } = JSON.parse(response?.error!) as AuthError;
        dispatch(
          setAlertMessage({
            icon: IconsEnum.CLOSE_CIRCLE,
            message:
              data.message === AuthErrorType.InvalidGrant
                ? usernamePasswordError
                : data.message === AuthErrorType.BlockedAccount
                ? blockedAccountLoginError
                : data.message,
            color: 'var(--color-warning)',
          })
        );
      }

      // address just history widhout reloadinging the page
      history.replaceState(history.state, '', `${path}${seamless ? '?seamless': ''}`)

      try {
        // unlock other api calls during the login intervall
        await mutex.release();
      } catch (ex) {
        console.log('mutex-exc', ex);
      }

      return response;
    },
    [path, blockedAccountLoginError, usernamePasswordError, dispatch]
  );

  useEffect(() => {
    if(skip) {return}

    const username = params.get('snai-carta');
    const password = params.get('snai-token');
    const clientType = params.get('client-type');

    if (isNotEmpty(username) && isNotEmpty(password) && isNotEmpty(clientType)) {
      doAuthenticate(username, password, clientType);
    }
  }, [skip, params, doAuthenticate]);

  return null;
};